/**
 * Dynamic favicon from console whitelabel API (JSON + postMessage iframe flows).
 * No auth required. Falls back to CRA default favicon on missing URL or errors.
 */

const defaultFaviconHref = () =>
    `${process.env.PUBLIC_URL != null ? process.env.PUBLIC_URL : ""}/favicon.ico`;

const whitelabelGetDataBase = () => {
    const raw = process.env.REACT_APP_WHITELABEL_API_BASE || "https://api.spyne.ai";
    return raw.replace(/\/+$/, "");
};

function pickEnterpriseIdFromProductRoots(data) {
    if (!Array.isArray(data)) return null;
    for (const item of data) {
        if (!item || typeof item !== "object") continue;
        const id = item.enterpriseId;
        if (id != null && String(id).trim() !== "") {
            return String(id).trim();
        }
    }
    return null;
}

function pickEnterpriseIdFromParams(params) {
    const id = params?.enterpriseId;
    if (id != null && String(id).trim() !== "") {
        return String(id).trim();
    }
    return null;
}

function pickEnterpriseIdFromPostMessage(envelope) {
    if (!envelope || typeof envelope !== "object") return null;
    const id = envelope.enterpriseId ?? envelope.enterprise_id;
    if (id != null && String(id).trim() !== "") {
        return String(id).trim();
    }
    return null;
}

function resolveEnterpriseIdForWhitelabel(productDataArray, params, postMessageEnvelope) {
    return (
        pickEnterpriseIdFromProductRoots(productDataArray) ||
        pickEnterpriseIdFromParams(params) ||
        pickEnterpriseIdFromPostMessage(postMessageEnvelope) ||
        null
    );
}

function clearDocumentFaviconLinks() {
    if (typeof document === "undefined") return;
    document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach((el) => el.remove());
}

const TRANSPARENT_FAVICON_HREF =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

function isSmartViewV3ViewerPath() {
    if (typeof window === "undefined") return false;
    const path = window.location.pathname.replace(/\/+$/, "") || "/";
    const q = new URLSearchParams(window.location.search);
    return path === "/360" && (q.get("version") || "").toLowerCase() === "v3";
}

export function setDocumentFaviconToDefault() {
    if (typeof document === "undefined") return;
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }
    link.href = defaultFaviconHref();
}

export function initializeDocumentFavicon() {
    if (typeof document === "undefined") return;
    if (isSmartViewV3ViewerPath()) {
        clearDocumentFaviconLinks();
        const link = document.createElement("link");
        link.rel = "icon";
        link.href = TRANSPARENT_FAVICON_HREF;
        document.head.appendChild(link);
        return;
    }
    setDocumentFaviconToDefault();
}

export async function applyWhitelabelFaviconFromProductData(productDataArray, params, postMessageEnvelope) {
    const enterpriseId = resolveEnterpriseIdForWhitelabel(productDataArray, params, postMessageEnvelope);
    if (!enterpriseId) {
        if (isSmartViewV3ViewerPath()) {
            return;
        }
        setDocumentFaviconToDefault();
        return;
    }

    const url = `${whitelabelGetDataBase()}/console/v1/whitelabel/get-data?enterprise_id=${encodeURIComponent(
        enterpriseId
    )}`;

    try {
        const response = await fetch(url, {
            headers: { Accept: "application/json, text/plain, */*" },
        });
        if (!response.ok) {
            setDocumentFaviconToDefault();
            return;
        }
        const json = await response.json();
        const fav = json?.fav_icon_url;
        if (typeof fav === "string" && fav.trim()) {
            const href = fav.trim();
            let link = document.querySelector('link[rel="icon"]');
            if (!link) {
                link = document.createElement("link");
                link.rel = "icon";
                document.head.appendChild(link);
            }
            link.href = href;
        } else {
            setDocumentFaviconToDefault();
        }
    } catch {
        setDocumentFaviconToDefault();
    }
}
